export const locales = ['en', 'de', 'ru'] as const;
export type Locale = (typeof locales)[number];

const localeStorageKey = 'a-vernalis-locale';
const cookieLifetime = 60 * 60 * 24 * 365;

export const mosaicCopy = {
  en: {
    chooseLanguage: 'Choose language',
    switchToLight: 'Switch to light theme',
    switchToDark: 'Switch to dark theme',
    back: 'BACK',
    miscellanea: 'MISC.',
    connection: {
      connecting: 'CONNECTING',
      connected: 'CONNECTED',
      offline: 'OFFLINE'
    },
    home: {
      eyebrow: 'A—Vernalis · Multi-screen study I',
      title: ['One scene.', 'Many surfaces.'],
      lead: 'Compose nearby phones, tablets, and computers into one continuous surface. Nothing to install: connect, place, and begin.',
      desk: 'Session desk · 01',
      create: 'Create private mosaic',
      demo: 'Open a personal demo',
      include: 'Include this device in the mosaic',
      includeNote: 'Keep it off when this device is only the control desk.',
      orJoin: 'or join one',
      joinLabel: 'Join an existing mosaic · six-character code',
      join: 'Join mosaic',
      stepsLabel: 'How ScreenMosaic works',
      steps: [
        ['CONNECT', 'Scan the private invitation or share it with AirDrop and Nearby Share.'],
        ['PLACE', 'Read each device angle, then refine its exact position on the map.'],
        ['PERFORM', 'Play generated scenes, photographs, GIFs, or synchronized video.']
      ]
    },
    pending: {
      eyebrow: 'Screen approval · match these digits',
      lead: 'Find this code at the control desk and approve it. This screen connects automatically; unanswered requests expire after five minutes.',
      cancel: 'Cancel request'
    }
  },
  de: {
    chooseLanguage: 'Sprache wählen',
    switchToLight: 'Zum hellen Design wechseln',
    switchToDark: 'Zum dunklen Design wechseln',
    back: 'ZURÜCK',
    miscellanea: 'VERSCH.',
    connection: {
      connecting: 'VERBINDUNG',
      connected: 'VERBUNDEN',
      offline: 'OFFLINE'
    },
    home: {
      eyebrow: 'A—Vernalis · Mehrdisplay-Studie I',
      title: ['Eine Szene.', 'Viele Flächen.'],
      lead: 'Verbinde Smartphones, Tablets und Computer zu einer durchgehenden Fläche. Keine Installation: verbinden, anordnen und beginnen.',
      desk: 'Sitzungsdesk · 01',
      create: 'Privates Mosaik erstellen',
      demo: 'Persönliche Demo öffnen',
      include: 'Dieses Gerät ins Mosaik aufnehmen',
      includeNote: 'Deaktiviert lassen, wenn dieses Gerät nur als Steuerpult dient.',
      orJoin: 'oder beitreten',
      joinLabel: 'Bestehendem Mosaik beitreten · sechsstelliger Code',
      join: 'Mosaik beitreten',
      stepsLabel: 'So funktioniert ScreenMosaic',
      steps: [
        ['VERBINDEN', 'Private Einladung scannen oder per AirDrop und Nearby Share teilen.'],
        [
          'ANORDNEN',
          'Winkel der Geräte erfassen und ihre genaue Position auf der Karte verfeinern.'
        ],
        ['ABSPIELEN', 'Generierte Szenen, Fotos, GIFs oder synchronisierte Videos wiedergeben.']
      ]
    },
    pending: {
      eyebrow: 'Display-Freigabe · Ziffern abgleichen',
      lead: 'Diesen Code am Steuerpult suchen und bestätigen. Das Display verbindet sich automatisch; unbeantwortete Anfragen verfallen nach fünf Minuten.',
      cancel: 'Anfrage abbrechen'
    }
  },
  ru: {
    chooseLanguage: 'Выбрать язык',
    switchToLight: 'Переключить на светлую тему',
    switchToDark: 'Переключить на тёмную тему',
    back: 'НАЗАД',
    miscellanea: 'РАЗНОЕ',
    connection: {
      connecting: 'ПОДКЛЮЧЕНИЕ',
      connected: 'ПОДКЛЮЧЕНО',
      offline: 'НЕ В СЕТИ'
    },
    home: {
      eyebrow: 'A—Vernalis · Многоэкранный этюд I',
      title: ['Одна сцена.', 'Много поверхностей.'],
      lead: 'Объедините соседние телефоны, планшеты и компьютеры в одну непрерывную поверхность. Ничего устанавливать не нужно: подключите, разместите и начинайте.',
      desk: 'Пульт сеанса · 01',
      create: 'Создать приватную мозаику',
      demo: 'Открыть личную демоверсию',
      include: 'Включить это устройство в мозаику',
      includeNote: 'Оставьте выключенным, если устройство служит только пультом управления.',
      orJoin: 'или присоединиться',
      joinLabel: 'Присоединиться к мозаике · шестизначный код',
      join: 'Присоединиться',
      stepsLabel: 'Как работает ScreenMosaic',
      steps: [
        [
          'ПОДКЛЮЧИТЬ',
          'Отсканируйте приватное приглашение или передайте его через AirDrop и Nearby Share.'
        ],
        ['РАЗМЕСТИТЬ', 'Определите наклон устройств и уточните их точное положение на карте.'],
        ['ЗАПУСТИТЬ', 'Показывайте сгенерированные сцены, фотографии, GIF или синхронное видео.']
      ]
    },
    pending: {
      eyebrow: 'Подтверждение экрана · сверьте цифры',
      lead: 'Найдите этот код на пульте управления и подтвердите его. Экран подключится автоматически; запрос истечёт через пять минут.',
      cancel: 'Отменить запрос'
    }
  }
} as const;

export function isLocale(value: string | null | undefined): value is Locale {
  return value != null && locales.includes(value as Locale);
}

export function resolveLocale(
  cookieLocale: string | null | undefined,
  storedLocale: string | null | undefined,
  browserLocale: string | null | undefined
): Locale {
  if (isLocale(cookieLocale)) return cookieLocale;
  if (isLocale(storedLocale)) return storedLocale;
  const language = browserLocale?.toLowerCase().split('-')[0];
  return isLocale(language) ? language : 'en';
}

export function readLocale(): Locale {
  let cookieLocale: string | undefined;
  let storedLocale: string | null = null;
  try {
    cookieLocale = document.cookie
      .split('; ')
      .find((entry) => entry.startsWith(`${localeStorageKey}=`))
      ?.split('=')[1];
    storedLocale = localStorage.getItem(localeStorageKey);
  } catch {
    // Preference storage can be disabled by the browser.
  }
  return resolveLocale(cookieLocale, storedLocale, navigator.language);
}

export function persistLocale(locale: Locale) {
  document.documentElement.lang = locale;
  try {
    localStorage.setItem(localeStorageKey, locale);
  } catch {
    // Storage can be disabled by the browser.
  }

  const sharedDomain = location.hostname.endsWith('a-vernalis.com')
    ? '; Domain=.a-vernalis.com'
    : '';
  try {
    document.cookie = `${localeStorageKey}=${locale}; Path=/; Max-Age=${cookieLifetime}; SameSite=Lax; Secure${sharedDomain}`;
  } catch {
    // Cookie access can be disabled by the browser.
  }
}
